import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";

import {
  Button,
  Card,
  CardStackSection,
  Heading,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { minBirthDate, required, validDate } from "@origin-digital/validation";
import { reportException } from "@origin-digital/reporting-client";

import { useTrackingContext } from "src/context/TrackingContext";
import { useTextFieldState } from "src/hooks";
import { pageRoutes } from "src/consts";
import {
  formatDobApi,
  reduceValidators,
  transformDate,
} from "src/utils/helpers";
import { errorTracking, formTracking } from "src/utils/tracking";
import {
  hideNotification,
  showErrorNotification,
} from "src/utils/notifications";
import { Form } from "src/components/Form";
import { TextField } from "src/components/TextField";

import { FieldUpdateHook } from "../../../crm/profileFieldMap";
import { ProfileFieldMapTypes } from "../../../crm/profileFieldMapContextHelpers";
import content from "./content.json";

export const UpdateDobScreen: React.FC<{
  useUpdate: FieldUpdateHook<string>;
  type: ProfileFieldMapTypes;
}> = ({ useUpdate, type }) => {
  const [updateDob, { success, loading, error }] = useUpdate();
  const trackingContext = useTrackingContext();
  const dobState = useTextFieldState("", content.dob.helperText);
  const [redirecting, setRedirecting] = useState<boolean>(false);

  useEffect(() => {
    if (error) {
      showErrorNotification(content.submitError);
      errorTracking(error, type);
      reportException("UpdateDobScreen.updateDob()", error);
    }
  }, [error]);

  useEffect(() => {
    if (success && !error) {
      formTracking("complete", trackingContext, type);
      setRedirecting(true);
    }
  }, [success, error]);

  const handleValidate = (): string => {
    const { errors } = content.dob;

    const validators = [
      required({ error: errors.required }),
      validDate({ error: errors.invalidFormat }),
      minBirthDate({ error: errors.minDate, minAge: 16 }),
    ];

    const errorMessage = reduceValidators(dobState.value, validators);

    dobState.setError(errorMessage);

    return errorMessage;
  };

  const handleSubmit = () => {
    formTracking("start", trackingContext, type);

    if (handleValidate()) {
      return;
    }

    dobState.setFocus(false);
    hideNotification();

    updateDob(formatDobApi(dobState.value));
  };

  if (redirecting) {
    return <Redirect push to={pageRoutes.updateDob.success} />;
  }

  return (
    <CardStackSection data-id="update-dob">
      <Card>
        <Form onSubmit={handleSubmit}>
          <Stack space={["large", "xlarge"]}>
            <Heading data-id="heading" variant="h3">
              {content.heading}
            </Heading>
            <Stack space={["medium", "large"]}>
              <Text data-id="dob-message">{content.message}</Text>
              <TextField
                characterCount
                disabled={loading}
                handleValidate={handleValidate}
                id="dob"
                label={content.dob.label}
                maxLength={10}
                textFieldState={dobState}
                transform={transformDate}
                type="tel"
                onFocus={() => formTracking("start", trackingContext, type)}
              />
            </Stack>
            <Button data-id="submit" fullWidth type="submit" spinner={loading}>
              {content.submit}
            </Button>
          </Stack>
        </Form>
      </Card>
    </CardStackSection>
  );
};
